import * as React from 'react';

import type * as api from '@gitbook/api-client';

import type { UpgradeDialogInitialState } from './useUpgradeDialogState';
import type { UpgradeDialogReason } from './useUpgradeOrganizationPlan';

/**
 * State used to render a locally mounted upgrade dialog.
 */
export type UpgradeDialogState = {
    organization: api.Organization;
    targetProduct: api.BillingProduct.Pro2024;
    reason: UpgradeDialogReason;
    initialState?: UpgradeDialogInitialState;
    onSuccess?: (request: api.OperationSpec<'upgradeOrganizationPlan'>['RequestBody']) => void;
    analyticsContext?: {
        screenTab?: string;
        siteId?: api.Site['id'];
        siteType?: api.SiteType;
        siteAppliedType?: api.SiteType;
        isFrozen?: boolean;
    };
};

type UpgradeDialogContextValue = {
    openUpgradeDialog: (initialState: UpgradeDialogState) => void;
    /** Explain that AI can't be enabled for the organization. */
    openAIRestrictedDialog: () => void;
};

/**
 * Context used by local owners to expose an upgrade dialog to descendants.
 */
export const UpgradeDialogContext = React.createContext<UpgradeDialogContextValue | null>(null);

/**
 * Access the local upgrade dialog context.
 */
export function useUpgradeDialogContext(): UpgradeDialogContextValue {
    const context = React.useContext(UpgradeDialogContext);
    if (!context) {
        throw new Error('useUpgradeDialogContext must be used within an UpgradeDialogProvider');
    }

    return context;
}
